export const endpoints = {
  // Tracking realm (shared with Tracking Mobile, `aud: tracking`). See ADR-0005.
  auth: {
    /**
     * The signed-in grower. **Not** `/auth/tracking/me` — that route does not exist on the tracking
     * realm and answers `404 NOT_FOUND "Cannot GET /api/v1/auth/tracking/me"` (only the *admin*
     * realm has an `auth/.../me`). Confirmed against `GET /api/docs-json`, 2026-09-02.
     *
     * It is the same route `profile.me` points at, and deliberately so: one payload carries the
     * identity `<AuthProvider>` needs and the counters the profile menu renders.
     */
    me: '/profile/tracking',
    login: '/auth/tracking/login',
    logout: '/auth/tracking/logout',
    refresh: '/auth/tracking/refresh',
    signup: '/auth/tracking/signup',
    otpSend: '/auth/tracking/login/otp/send',
    otpVerify: '/auth/tracking/login/otp/verify',
    otpResend: '/auth/tracking/login/otp/resend',
    forgotSend: '/auth/tracking/forgot-password/otp/send',
    forgotVerify: '/auth/tracking/forgot-password/otp/verify',
    forgotResend: '/auth/tracking/forgot-password/otp/resend',
    forgotReset: '/auth/tracking/forgot-password/reset',
    /**
     * ADR-0029 — confirmed live (2026-10-14). Path corrected to match greenway_mobile's
     * `account_access.dart`, which records this shape as **confirmed** against the "Sprint 8 -
     * Stubs" Postman collection (2026-10-08): `POST .../account/select`, not the earlier
     * FE-guessed `.../switch-account` (which the live backend 404s).
     */
    switchAccount: '/auth/tracking/account/select',
  },

  // Map — tracking realm. Real, deployed contracts (not placeholders): verified against the live
  // API 2026-08-25. See docs/features/113582-map-and-device-behavior/spec.md § Contract.
  map: {
    /** The Reference location fallback chain, whole and unresolved (113584 §1). */
    referenceLocation: '/map/tracking/reference-location',
    /**
     * The Grower's own Fields and Devices matching `q` (113585). One flat list, ordered by name
     * ascending, with each row's two display lines already composed server-side. `q` is capped at
     * 100 characters by the contract. Device **category is not matched** — confirmed against the
     * live API 2026-08-25.
     */
    search: '/map/tracking/search',
  },

  // ── FieldPro placeholder endpoint groups (paths firm up per feature) ──────────
  // Device paths are CONFIRMED against the live backend — the Tracking Mobile app probed every
  // one of these with a real grower token (2026-08-13/18/21) and captured the payloads in
  // greenway_mobile/docs/specs/features/devices/{device-listing,device-details}/api-contract.md.
  // `VITE_API_URL` already carries the `/api/v1` prefix, so these are relative to that.
  devices: {
    /** Cursor-paginated list. Search, category, filters, sort and paging are all server-side. */
    list: '/devices/tracking/list',
    /** The grower's own device categories — the listing's quick-filter tabs. Per account. */
    categories: '/devices/tracking/meta/categories',
    detail: (id: string) => `/devices/tracking/${id}`,
    /** POST { type: 'TURN_ON' | 'TURN_OFF' } → 201. Note SCREAMING_SNAKE on the request only. */
    commands: (id: string) => `/devices/tracking/${id}/commands`,
    /** DELETE a queued command. Needs the command's own id, not just the device's. */
    command: (id: string, commandId: string) => `/devices/tracking/${id}/commands/${commandId}`,
    /** PATCH { customName } — `null` clears it and reverts to the default name. */
    rename: (id: string) => `/devices/tracking/${id}/rename`,
    /** GET (null when none) / PUT / DELETE the irrigation schedule. */
    schedule: (id: string) => `/devices/tracking/${id}/schedule`,
    /** GET ?range=today|7d|30d|1y|all — server-side aggregation per range. */
    flowRate: (id: string) => `/devices/tracking/${id}/flow-rate`,
    /** GET the active timer / POST { durationMinutes: 1..1440 } to start one. */
    stopTimer: (id: string) => `/devices/tracking/${id}/stop-timer`,
    /**
     * GET the device's event log — Show History (US-101880). Real and deployed: the path, the
     * `limit`/`cursor` paging, the `eventTypes` enum and the `startDate`/`endDate` window are all
     * in the published OpenAPI document. Only the 200's body is undocumented there; its envelope
     * was captured live 2026-08-25 (see `features/device-history/schemas.ts`).
     */
    history: (id: string) => `/devices/tracking/${id}/history`,
    /**
     * GET the device's alerts — View Alerts (US-113523). **No such route exists yet.** Every path
     * in `GET /api/docs-json` was walked 2026-08-28 and the spec contains no device-alerts route
     * at any version, so this is the path the feature is written against and MSW answers it. See
     * ADR-0021.
     */
    alerts: (id: string) => `/devices/tracking/${id}/alerts`,
    /** Device pins with Field association, for the Map (113582, ticket 04). Called with
     *  **no `bbox`** — a viewport slice cannot deliver 113586 §11c's exact cluster counts
     *  (ADR-0016 § Loading). */
    map: '/devices/tracking/map',
    /** The Device quick view's whole payload (113588, ticket 11) — pre-formatted values and
     *  pre-thresholded indicator states, so the panel computes nothing (§17a). */
    mapPopup: (id: string) => `/devices/tracking/${id}/map-popup`,
    /**
     * Everything the **Remap Device page** needs on load (119175 §2, 119176 §7b) — which Device
     * and Field to highlight, the stored position, and what to frame on. REAL and published:
     * `DeviceRemapController` in `greenway_api` names these two stories in its own docblock, and
     * the route is in the live `/api/docs-json` (fetched 2026-09-11).
     *
     * Deliberately does NOT carry the other Devices and Fields — those are the shipped
     * `/devices/tracking/map` and `/fields/tracking`, so one pin never has two sources free to
     * disagree.
     */
    remapContext: (id: string) => `/devices/tracking/${id}/remap-context`,

    /** The **placement verdict** probe. A POST that writes nothing — it is called on every canvas
     *  click and every coordinate commit, because USA containment is not answerable client-side. */
    validateLocation: (id: string) => `/devices/tracking/${id}/location/validate`,

    /** **Save Mapping** — the only write in the Remap Device feature. Commits the Device's pending
     *  position. Everything else on that screen is client state until this succeeds. */
    location: (id: string) => `/devices/tracking/${id}/location`,
  },
  telemetry: {
    latest: (id: string) => `/telemetry/tracking/devices/${id}/latest`,
    history: (id: string) => `/telemetry/tracking/devices/${id}/history`,
    activityCalendar: (id: string) => `/telemetry/tracking/devices/${id}/activity-calendar`,
  },
  // Verified against the deployed OpenAPI document, 2026-08-25. The `/fields/fields` paths these
  // replaced were scaffold placeholders and never existed.
  fields: {
    /** Every saved Field with Boundary geometry. Called with no `search` and no `bbox`. */
    list: '/fields/tracking',
    detail: (id: string) => `/fields/tracking/${id}`,
    import: '/fields/tracking/import',
    validateBoundary: '/fields/tracking/validate-boundary',
  },
  wells: {
    list: '/fields/tracking/wells',
    detail: (id: string) => `/fields/tracking/wells/${id}`,
    pumps: (id: string) => `/fields/tracking/wells/${id}/pumps`,
  },
  subscriptions: {
    /**
     * GET ?status=all|active|expired&search=&limit=&cursor=&renewalType=one_year|december —
     * tab filter, keyword search and cursor pagination are all server-side (US-113569).
     */
    list: '/subscriptions/tracking',
    /** GET one subscription with its timeline (US-113569 § Details). */
    detail: (id: string) => `/subscriptions/tracking/${id}`,
    /**
     * POST { renewalType, customerDeviceIds } — the renewal coverage preview (US-113571).
     * A **POST** despite being a read, because the device-id set is the request body.
     * 403 = a device the grower does not own; 404 = a device with no subscription.
     */
    renewalSummary: '/subscriptions/tracking/renewal-summary',
    /**
     * POST { renewalType, customerDeviceIds, billingAddressId, autoRenewDeviceIds } — the
     * Checkout tax quote. A pure read; 404 `ADDRESS_NOT_FOUND`, 502 `TAX_CALCULATION_FAILED`.
     */
    checkoutSummary: '/subscriptions/tracking/renewal/checkout-summary',
    /**
     * POST checkout-summary body + `paymentMethodId` — charge the renewal. 200 carries the Payment
     * Completed shape; `clientSecret` beside `paymentStatus: "processing"` = a 3DS challenge.
     * 402 `PAYMENT_FAILED`, 502 `PAYMENT_GATEWAY_ERROR` / `TAX_CALCULATION_FAILED`, 409
     * `RENEWAL_IN_PROGRESS`, 422 `RENEWAL_TOTAL_EXCEEDS_LIMIT`, 400 `CARD_EXPIRED`.
     */
    pay: '/subscriptions/tracking/renewal/pay',
    /** PATCH { enabled } — 409 when already in the requested state or not active (US-117866). */
    autoRenew: (id: string) => `/subscriptions/tracking/${id}/auto-renew`,
    /**
     * GET ?billingAddressId= — the enrolment preview (US-101879). Without an address: no tax/total.
     * 409 SUBSCRIPTION_ALREADY_AUTO_RENEWING / SUBSCRIPTION_NOT_ELIGIBLE; 503 TAX_CALCULATION_FAILED.
     */
    autoRenewPreview: (id: string) => `/subscriptions/tracking/${id}/auto-renew/preview`,
    /**
     * POST { paymentMethodId, billingAddressId } — enrol auto-renewal (US-101879). Charges nothing.
     * 409 SUBSCRIPTION_ALREADY_AUTO_RENEWING / SUBSCRIPTION_NOT_ELIGIBLE; 400 CARD_EXPIRED /
     * CARD_EXPIRES_BEFORE_RENEWAL / AUTO_RENEWAL_REQUIRES_CARD; 404 PAYMENT_METHOD_NOT_FOUND /
     * ADDRESS_NOT_FOUND.
     */
    autoRenewEnrol: (id: string) => `/subscriptions/tracking/${id}/auto-renew/enrol`,
    /** POST — 409 when already cancelled or expired. */
    cancel: (id: string) => `/subscriptions/tracking/${id}/cancel`,
    /**
     * GET ?cursor= — one Device's invoices, newest first (US-117762). Keyed on the **device**, not
     * the subscription: a term maps to one invoice, so a term-scoped list could never show both.
     */
    deviceInvoices: (customerDeviceId: string) =>
      `/subscriptions/tracking/devices/${customerDeviceId}/invoices`,
    /** GET — one renewal payment's result (Payment Completed). 404 `RENEWAL_NOT_FOUND`. */
    renewal: (renewalId: string) => `/subscriptions/tracking/renewal/${renewalId}`,
    /** GET — the PDF for one renewal payment (Payment Completed, ticket 08). */
    renewalInvoice: (renewalId: string) => `/subscriptions/tracking/renewal/${renewalId}/invoice`,
  },
  invoices: {
    /**
     * GET — the stored PDF behind a View Invoices row (US-117762). A "Subscription Purchased" row
     * yields the webstore order invoice; the client needs no branch.
     */
    download: (invoiceId: string) => `/invoices/tracking/${invoiceId}`,
  },
  support: {
    tickets: '/support/tracking/tickets',
    detail: (id: string) => `/support/tracking/tickets/${id}`,
    messages: (id: string) => `/support/tracking/tickets/${id}/messages`,
    raise: '/support/tracking/tickets',
  },
  // Warranty claims — CONFIRMED against the live backend. WarrantyClaimTrackingController
  // (apps/api/src/warranty/tracking/warranty-claim.controller.ts) mounts at `warranty/tracking`,
  // not the earlier FE-guessed `warranty-claims/tracking` — that prefix predates the backend and
  // was never a real route. Only `deviceClaims` (device-scoped, under `devices/tracking/`) ever
  // matched by coincidence; the other four calls 404'd against anything but MSW.
  warranty: {
    /** GET ?page&pageSize — one Device's claims, ordered `requested_at DESC` **by the server**. */
    deviceClaims: (customerDeviceId: string) =>
      `/devices/tracking/${customerDeviceId}/warranty-claims`,
    /**
     * One path, two verbs — deliberately one key, because a second constant holding the identical
     * literal can only ever drift.
     *
     * GET ?page&pageSize — every claim across the account, same shape and same server-side
     * ordering as `deviceClaims`.
     * POST `{ customerDeviceId, reason, description, imageAssetIds }` -> 201.
     */
    claims: '/warranty/tracking/claims',
    /** GET — the Devices a new claim may be raised against right now. Server verdict, no filter. */
    eligibleDevices: '/warranty/tracking/eligible-devices',
    /** GET — one claim's full detail, behind the Ticket Preview (ADR-0019). */
    claim: (claimId: string) => `/warranty/tracking/claims/${claimId}`,
  },
  // Notifications — CONFIRMED against the live backend. Tracking Mobile probed every route below
  // with a real grower token on 2026-08-28 and re-read the push-token DTOs off `GET /api/docs-json`
  // on 2026-08-31; see greenway_mobile/docs/MOCKS.md §8. The published spec documents them in prose
  // with **no response schema**, so the payload shapes were captured from live responses (seeded
  // through the backend's own `test-push` route) and `schemas.ts` parses them tolerantly.
  //
  // These replace an earlier set of guesses taken from mobile's *superseded* PROPOSED contract
  // (`.../notifications`, `.../{id}/read`), which never existed on the server.
  notifications: {
    /** GET `?limit=&cursor=` — newest first, cursor-paginated. `meta` sits **beside** `data`. */
    list: '/notifications/tracking',
    /** POST, empty body. Marks **every** unread notification read; accepts no ids. */
    markRead: '/notifications/tracking/mark-read',
    /** GET → `{ hasUnread }`. The cheap read for the bell, without pulling the list. */
    unread: '/notifications/tracking/unread',
    /**
     * Preferences — GET and PATCH. This **is** on the backend after all: `GET /api/docs-json` on
     * 2026-09-02 lists `/notifications/tracking/preferences` with both verbs. Both apps had it
     * recorded as absent (mobile still mocks it), which is why this app was calling a
     * `/settings` path that never existed.
     *
     * Note the wire spells the third category **`requestsClaims`**, not `requestsAndClaims`.
     */
    settings: '/notifications/tracking/preferences',
  },
  profile: {
    /** GET — identity + `deviceCounts`, `supportTickets`, `support`. PUT updates it. */
    me: '/profile/tracking',
    /** The in-session Change Password flow (US-117548). No current password at any step. */
    changePassword: {
      sendOtp: '/profile/tracking/change-password/send-otp',
      resendOtp: '/profile/tracking/change-password/resend-otp',
      verifyOtp: '/profile/tracking/change-password/verify-otp',
      /** PUT { newPassword } — no confirmPassword. Revokes no session. */
      update: '/profile/tracking/change-password',
    },
    /** The two-code Phone change (US-117543 §E–H). */
    changePhone: {
      current: {
        sendOtp: '/profile/tracking/change-phone/current/send-otp',
        resendOtp: '/profile/tracking/change-phone/current/resend-otp',
        verifyOtp: '/profile/tracking/change-phone/current/verify-otp',
      },
      new: {
        sendOtp: '/profile/tracking/change-phone/new/send-otp',
        resendOtp: '/profile/tracking/change-phone/new/resend-otp',
        verifyOtp: '/profile/tracking/change-phone/new/verify-otp',
      },
    },
  },
  addresses: {
    /**
     * GET — saved addresses, default first. REAL. Takes `limit` (default 40, a multiple of 10) and
     * an opaque `cursor`; returns `{ data, meta: { totalItems, nextCursor } }`.
     */
    list: '/profile/tracking/address',
    /** POST { title, line1, line2?, city, state, zipcode, isDefault? } → 201. REAL. */
    create: '/profile/tracking/address',
    /** GET ?zipcode= → { city, state, cities[] }. REAL. 422 = no match; 503 = provider unreachable. */
    zipLookup: '/profile/tracking/address/zip-lookup',
    /** PUT — REAL. */
    update: (id: string) => `/profile/tracking/address/${id}`,
    /** DELETE — REAL. */
    remove: (id: string) => `/profile/tracking/address/${id}`,
    /** PATCH — REAL. */
    makeDefault: (id: string) => `/profile/tracking/address/${id}/default`,
  },
  // Associate Growers — US-119181 (list) / US-119183 (create) / US-119185 (edit, status, delete).
  // CONFIRMED against the "Sprint 8 - Stubs" Postman collection's Tracking surface (updated
  // 2026-10-08), mirrored from greenway_mobile's `associate_grower_repository.dart`, which already
  // ships this exact contract against the same stub collection.
  associateGrowers: {
    /** GET ?page&limit&search — newest-first, 8 per page (US-119181 §6a). */
    list: '/associate-growers/tracking',
    /** POST { firstName, lastName, phone } → always comes back `status: "invited"`. */
    create: '/associate-growers/tracking',
    detail: (id: string) => `/associate-growers/tracking/${id}`,
    /** PUT — full replacement of the three editable fields; `status` is never accepted here. */
    update: (id: string) => `/associate-growers/tracking/${id}`,
    /** DELETE — soft delete. */
    remove: (id: string) => `/associate-growers/tracking/${id}`,
    /** PATCH { status: 'active' | 'inactive' } — enable/disable (US-119185 §7-8). */
    status: (id: string) => `/associate-growers/tracking/${id}/status`,
    /** GET ?phone&excludeId — on-blur duplicate check (US-119183 §4e / US-119185 §2d-e). */
    phoneCheck: '/associate-growers/tracking/phone-check',
  },
  paymentMethods: {
    /**
     * GET — every saved card. **No ordering guarantee**: US-117846 Rule 13 states no ordering rule
     * applies and explicitly forbids the client from pinning the default to the top (unlike the
     * address list, which the AC *does* pin). Render whatever order the server returns.
     */
    list: '/profile/tracking/payment-method',
    /** POST → { clientSecret, setupIntentId } for Stripe card tokenization. */
    setupIntent: '/profile/tracking/payment-method/setup-intent',
    /** POST { setupIntentId, setAsDefault? } — confirm a card save after tokenization. */
    confirm: '/profile/tracking/payment-method/confirm',
    /** PATCH — promote one card to the account default. */
    makeDefault: (id: string) => `/profile/tracking/payment-method/${id}/default`,
    /** DELETE — remove a saved card. */
    remove: (id: string) => `/profile/tracking/payment-method/${id}`,
  },
  storage: {
    presignedUrl: '/storage/tracking/pre-signed-url',
  },
} as const;
